// pages/DwiSendLogistics.jsx
// Admin → DwiSend → Logistics (Phase 1, RECORDS ONLY — owner decisions
// 2026-10-29): places (one tree for regions, districts, cities, towns,
// terminals, zones), transport partners + vehicles the owner enters, routes
// (with stops, partners, availability and INDICATIVE fees), manual dispatch,
// and a ledger of what partners are owed. No money moves here; an indicative
// fee never sets an order's delivery price. Behind dwisend_logistics (starts
// OFF). Nothing is deleted — places/routes/partners are hidden or stopped.
// Second person: a cross-border route and a "paid" mark each need a DIFFERENT
// DwiSend approver with an authenticator code — on the Approvals tab here, or
// /dwisend-logistics-approvals for approvers who aren't admins.
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { countryAPI, dwisendLogisticsAPI as api } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';

// Same fixed lists as the server (models/DwiSendLogistics.js) — the server checks them.
export const LOCATION_TYPES = ['region', 'district', 'city', 'town', 'terminal', 'zone'];
export const VEHICLE_TYPES = ['boda', 'car', 'pickup', 'van', 'truck', 'fuso', 'trailer', 'bus', 'other'];
const CHECKS = [['partners', 'Partners verified'], ['transport', 'Transport verified'], ['permits', 'Permits verified'], ['arrangements', 'Arrangements verified']];
const SUBJECTS = [['dwisend_order', 'DRC shop order (DSO-…)'], ['market_order', 'Uganda shop order (MKT-…)'], ['dwisend_request', 'DRC request (DRQ-…)']];
const ROUTE_COLOR = { draft: 'gray', pending_activation: 'amber', active: 'green', inactive: 'gray' };
const DISPATCH_COLOR = { assigned: 'cyan', in_transit: 'blue', delivered: 'green', cancelled: 'gray' };
const LEDGER_COLOR = { due: 'amber', pending_paid_approval: 'cyan', paid: 'green', reversed: 'gray' };
const label = s => String(s || '').replace(/_/g, ' ');

// Money: amounts travel in the currency's smallest unit (UGX 0 decimals, USD 2).
export function decimalsOf(currency) {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits; }
  catch { return 0; }
}
export function toMinor(text, currency) {
  const t = String(text ?? '').replace(/[\s,]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const d = decimalsOf(currency);
  const [whole, frac = ''] = t.replace('-', '').split('.');
  if (frac.length > d) return null;
  const n = Number(whole) * 10 ** d + Number((frac + '0'.repeat(d)).slice(0, d) || 0);
  return t.startsWith('-') ? -n : n;
}
export function money(minor, currency) {
  if (minor === null || minor === undefined) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'code' }).format(minor / 10 ** decimalsOf(currency)); }
  catch { return `${currency} ${minor}`; }
}
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const fail = err => toast.error(err.message);
const ask = q => { const r = window.prompt(q); return r && r.trim() ? r.trim() : null; };

const TABS = [['places', '📍 Places'], ['partners', '🚚 Partners & vehicles'], ['routes', '🛣️ Routes'], ['dispatch', '📦 Dispatch'], ['ledger', '📒 Ledger'], ['approvals', '✅ Approvals']];

export function LogisticsSection() {
  const [st, setSt] = useState(null);
  const [tab, setTab] = useState('places');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.status().then(setSt).catch(fail);
    countryAPI.adminList().then(r => setCountries(r.countries || [])).catch(fail);
  }, []);

  const flip = async on => {
    if (on && !window.confirm('Turn on DwiSend logistics? It only opens these admin screens (records only) — customers see nothing and no money moves.')) return;
    try { const r = await api.setSwitch(on); setSt(s => ({ ...s, enabled: r.enabled })); toast.success(on ? 'Logistics switched ON' : 'Switched OFF'); }
    catch (err) { fail(err); }
  };

  if (!st) return <div className="py-10 flex justify-center"><Spinner /></div>;
  if (!st.enabled) {
    return (
      <Card>
        <SectionHeader title="🚛 Logistics" />
        <p className="text-sm text-text-secondary mb-3">
          DwiSend logistics is <strong>switched OFF</strong>. Turning it on opens the admin screens for places, routes, transport
          partners, dispatch and the partner ledger. Records only — customers see nothing and no money moves.
        </p>
        <Button size="sm" onClick={() => flip(true)}>Turn on</Button>
      </Card>
    );
  }
  return (
    <Card>
      <SectionHeader title="🚛 Logistics (records only)" action={<Button size="sm" variant="ghost" onClick={() => flip(false)}>Turn off</Button>} />
      <div className="flex gap-2 flex-wrap mb-4">
        {TABS.map(([id, name]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border'}`}>{name}</button>
        ))}
      </div>
      {tab === 'places'    && <PlacesTab countries={countries} />}
      {tab === 'partners'  && <PartnersTab countries={countries} />}
      {tab === 'routes'    && <RoutesTab countries={countries} />}
      {tab === 'dispatch'  && <DispatchTab countries={countries} />}
      {tab === 'ledger'    && <LedgerTab countries={countries} />}
      {tab === 'approvals' && <ApprovalsList />}
    </Card>
  );
}

const currenciesOf = countries => [...new Set(countries.map(c => c.currency?.code).filter(Boolean))];

// ── Places ───────────────────────────────────────────────────────────────────
function PlacesTab({ countries }) {
  const [country, setCountry] = useState('UG');
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.locations({ countryCode: country }).then(r => setRows(r.locations || [])).catch(fail), [country]);
  useEffect(() => { setRows(null); load(); }, [load]);

  const toggle = async l => {
    if (l.isActive && !window.confirm(`Hide ${l.name}? Routes through it stop taking NEW dispatches; history stays.`)) return;
    try { await api.updateLocation(l._id, { isActive: !l.isActive }); load(); } catch (err) { fail(err); }
  };

  return (
    <div>
      <p className="text-xs text-text-muted mb-3">
        Every place DwiSend uses, per country. A place can sit under another (region → district → town → terminal, up to 7 levels)
        or straight under its country. Hiding a place blocks new dispatches through it; nothing is ever deleted.
      </p>
      <div className="flex gap-3 items-end mb-3 flex-wrap">
        <Select label="Country" value={country} onChange={e => setCountry(e.target.value)} className="min-w-[200px]">
          {countries.map(c => <option key={c.code} value={c.code}>{c.flag || ''} {c.name} ({c.code})</option>)}
        </Select>
        <Button size="sm" onClick={() => setEditing({})}>+ Add place</Button>
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Empty title="No places yet" description="Use “+ Add place”." /> : (
        <Table headers={['Place', 'Type', 'Inside', 'Routes', 'Status', '']}>
          {rows.map(l => (
            <tr key={l._id} className={l.isActive ? '' : 'opacity-50'}>
              <Td className="text-text-primary font-semibold">{l.name}{l.isHub ? ' ⭐ hub' : ''}</Td>
              <Td>{l.type}</Td>
              <Td>{l.parentName || '—'}</Td>
              <Td>{l.usedByRoutes || '—'}</Td>
              <Td><Badge label={l.isActive ? 'Active' : 'Hidden'} color={l.isActive ? 'green' : 'gray'} /></Td>
              <Td><div className="flex gap-1">
                <Button size="xs" variant="secondary" onClick={() => setEditing(l)}>Edit</Button>
                <Button size="xs" variant="ghost" onClick={() => toggle(l)}>{l.isActive ? 'Hide' : 'Restore'}</Button>
              </div></Td>
            </tr>
          ))}
        </Table>
      )}
      {editing && <PlaceModal place={editing} countryCode={country} places={rows || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PlaceModal({ place, countryCode, places, onClose, onSaved }) {
  const isNew = !place._id;
  const [f, setF] = useState({ name: place.name || '', type: place.type || 'town', parentId: place.parentId || '', isHub: !!place.isHub,
    sortOrder: place.sortOrder ?? 0, notes: place.notes || '' });
  const set = k => e => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    const body = { ...f, parentId: f.parentId || null, sortOrder: Number(f.sortOrder) || 0 };
    try { if (isNew) await api.createLocation({ ...body, countryCode }); else await api.updateLocation(place._id, body); toast.success('Saved'); onSaved(); }
    catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? `Add place — ${countryCode}` : `Edit ${place.name}`} size="sm">
      <div className="space-y-3">
        <Input label="Name" placeholder="e.g. Gulu" value={f.name} onChange={set('name')} />
        <Select label="Type" value={f.type} onChange={set('type')}>{LOCATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</Select>
        <Select label="Inside (optional)" value={f.parentId} onChange={set('parentId')}>
          <option value="">— straight under the country —</option>
          {places.filter(p => p._id !== place._id).map(p => <option key={p._id} value={p._id}>{p.name} ({p.type})</option>)}
        </Select>
        <label className="flex gap-2 text-sm text-text-secondary"><input type="checkbox" checked={f.isHub} onChange={set('isHub')} /> Hub (routes start here)</label>
        <Input label="Order (lower shows first)" type="number" value={f.sortOrder} onChange={set('sortOrder')} />
        <Input label="Notes (admin only)" value={f.notes} onChange={set('notes')} />
        <div className="flex gap-2"><Button onClick={save} disabled={!f.name.trim()}>{isNew ? 'Add' : 'Save'}</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

// ── Partners & vehicles ──────────────────────────────────────────────────────
function PartnersTab({ countries }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [vehicleFor, setVehicleFor] = useState(null);
  const load = useCallback(() => api.partners().then(r => setRows(r.partners || [])).catch(fail), []);
  useEffect(() => { load(); }, [load]);

  const suspend = async p => {
    if (p.status === 'approved') {
      const why = ask(`Why is ${p.name} suspended?`); if (!why) return;
      try { await api.updatePartner(p._id, { status: 'suspended', statusReason: why }); load(); } catch (err) { fail(err); }
    } else {
      try { await api.updatePartner(p._id, { status: 'approved' }); load(); } catch (err) { fail(err); }
    }
  };
  const vehicleOnOff = async v => { try { await api.updateVehicle(v._id, { isActive: !v.isActive }); load(); } catch (err) { fail(err); } };

  return (
    <div>
      <p className="text-xs text-text-muted mb-3">Transport partners you enter yourself (they don't log in yet). Phones, plates and notes are admin only.</p>
      <Button size="sm" className="mb-3" onClick={() => setEditing({})}>+ Add partner</Button>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Empty title="No partners yet" /> : rows.map(p => (
        <div key={p._id} className={`border border-border rounded-lg p-3 mb-2 ${p.status === 'approved' ? '' : 'opacity-60'}`}>
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-text-primary">{p.name} <Badge label={p.status} color={p.status === 'approved' ? 'green' : 'gray'} /></div>
              <div className="text-xs text-text-muted">{[p.contactName, ...(p.phones || [])].filter(Boolean).join(' · ') || '—'} · serves {(p.countryCodes || []).join(', ') || '—'}</div>
              {p.statusReason && <div className="text-xs text-amber-400">Suspended: {p.statusReason}</div>}
            </div>
            <div className="flex gap-1">
              <Button size="xs" variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
              <Button size="xs" variant="secondary" onClick={() => setVehicleFor(p)}>+ Vehicle</Button>
              <Button size="xs" variant="ghost" onClick={() => suspend(p)}>{p.status === 'approved' ? 'Suspend' : 'Re-approve'}</Button>
            </div>
          </div>
          {(p.vehicles || []).length > 0 && (
            <div className="flex gap-2 flex-wrap mt-2">
              {p.vehicles.map(v => (
                <button key={v._id} onClick={() => vehicleOnOff(v)} title={v.isActive ? 'Click to take off the road' : 'Click to put back on the road'}
                  className={`text-xs px-2 py-1 rounded border border-border ${v.isActive ? 'text-text-primary' : 'line-through text-text-muted'}`}>
                  {v.type} {v.plate}{v.capacityKg ? ` · ${v.capacityKg} kg` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      {editing && <PartnerModal partner={editing} countries={countries} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {vehicleFor && <VehicleModal partner={vehicleFor} onClose={() => setVehicleFor(null)} onSaved={() => { setVehicleFor(null); load(); }} />}
    </div>
  );
}

function PartnerModal({ partner, countries, onClose, onSaved }) {
  const isNew = !partner._id;
  const [f, setF] = useState({ name: partner.name || '', contactName: partner.contactName || '', phones: (partner.phones || []).join(', '),
    countryCodes: partner.countryCodes || [], notes: partner.notes || '' });
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const flipCountry = code => setF(x => ({ ...x, countryCodes: x.countryCodes.includes(code) ? x.countryCodes.filter(c => c !== code) : [...x.countryCodes, code] }));
  const save = async () => {
    try { if (isNew) await api.createPartner(f); else await api.updatePartner(partner._id, f); toast.success('Saved'); onSaved(); } catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? 'Add transport partner' : `Edit ${partner.name}`} size="sm">
      <div className="space-y-3">
        <Input label="Company or driver name" value={f.name} onChange={set('name')} />
        <Input label="Contact person" value={f.contactName} onChange={set('contactName')} />
        <Input label="Phones (comma separated)" placeholder="0772 000111, 0701 222333" value={f.phones} onChange={set('phones')} />
        <div className="text-xs text-text-secondary">Countries served
          <div className="flex gap-3 mt-1 flex-wrap">{countries.map(c => (
            <label key={c.code} className="flex gap-1"><input type="checkbox" checked={f.countryCodes.includes(c.code)} onChange={() => flipCountry(c.code)} /> {c.code}</label>
          ))}</div>
        </div>
        <Input label="Notes (admin only)" value={f.notes} onChange={set('notes')} />
        <div className="flex gap-2"><Button onClick={save} disabled={!f.name.trim()}>{isNew ? 'Add' : 'Save'}</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

function VehicleModal({ partner, onClose, onSaved }) {
  const [f, setF] = useState({ type: 'pickup', plate: '', capacityKg: '', notes: '' });
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try { await api.createVehicle(partner._id, { ...f, capacityKg: f.capacityKg === '' ? null : Number(f.capacityKg) }); toast.success('Vehicle added'); onSaved(); }
    catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title={`Add vehicle — ${partner.name}`} size="sm">
      <div className="space-y-3">
        <Select label="Type" value={f.type} onChange={set('type')}>{VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</Select>
        <Input label="Number plate" placeholder="UBA 123X" value={f.plate} onChange={set('plate')} />
        <Input label="Capacity kg (optional)" type="number" value={f.capacityKg} onChange={set('capacityKg')} />
        <Input label="Notes (admin only)" value={f.notes} onChange={set('notes')} />
        <div className="flex gap-2"><Button onClick={save} disabled={!f.plate.trim()}>Add</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

// ── Routes ───────────────────────────────────────────────────────────────────
function RoutesTab({ countries }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [checking, setChecking] = useState(null);
  const load = useCallback(() => api.routes().then(r => setRows(r.routes || [])).catch(fail), []);
  useEffect(() => { load(); }, [load]);

  const activate = async r => {
    const msg = r.crossBorder ? `Ask a second approver to activate ${r.label}?` : `Activate ${r.label}? It can then take dispatches.`;
    if (!window.confirm(msg)) return;
    try { const x = (await api.activateRoute(r._id)).route; toast.success(x.status === 'active' ? 'Active' : 'Sent for approval'); load(); } catch (err) { fail(err); }
  };
  const stop = async r => {
    const why = ask(`Why does ${r.label} stop? (it stops taking new dispatches)`); if (!why) return;
    try { await api.deactivateRoute(r._id, why); load(); } catch (err) { fail(err); }
  };

  return (
    <div>
      <p className="text-xs text-text-muted mb-3">
        Indicative fees are a reference for you only — they never change what a client pays. A cross-border route needs its 4 checks
        and a second approver before it can take dispatches.
      </p>
      <Button size="sm" className="mb-3" onClick={() => setEditing({})}>+ New route</Button>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Empty title="No routes yet" /> : (
        <Table headers={['Route', 'Status', 'Partners', 'Indicative fees', 'Availability', '']}>
          {rows.map(r => (
            <tr key={r._id}>
              <Td className="text-text-primary font-semibold">{r.label}{r.crossBorder && <span className="ml-1"><Badge label="cross-border" color="amber" /></span>}
                {!r.bookable && r.status === 'active' && <div className="text-xs text-amber-400">Blocked: {r.blockers.join('; ')}</div>}</Td>
              <Td><Badge label={label(r.status)} color={ROUTE_COLOR[r.status]} /></Td>
              <Td><div className="text-xs">{r.partners.map(p => p.name).join(', ') || '—'}</div></Td>
              <Td><div className="text-xs">{r.indicativeFees.map((f, i) => <div key={i}>{f.vehicleType}: {money(f.amountMinor, f.currency)}</div>)}{!r.indicativeFees.length && '—'}</div></Td>
              <Td><div className="text-xs">{[r.availability, r.typicalDays != null ? `${r.typicalDays} days` : ''].filter(Boolean).join(' · ') || '—'}</div></Td>
              <Td><div className="flex gap-1 flex-wrap">
                <Button size="xs" variant="secondary" onClick={() => setEditing(r)}>Edit</Button>
                {r.crossBorder && !['active', 'pending_activation'].includes(r.status) && <Button size="xs" variant="secondary" onClick={() => setChecking(r)}>Checks</Button>}
                {['draft', 'inactive'].includes(r.status) && <Button size="xs" onClick={() => activate(r)}>{r.crossBorder ? 'Ask approval' : 'Activate'}</Button>}
                {r.status !== 'inactive' && <Button size="xs" variant="ghost" onClick={() => stop(r)}>Stop</Button>}
              </div></Td>
            </tr>
          ))}
        </Table>
      )}
      {editing && <RouteModal route={editing} countries={countries} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {checking && <ChecksModal route={checking} onClose={() => { setChecking(null); load(); }} />}
    </div>
  );
}

function RouteModal({ route, countries, onClose, onSaved }) {
  const isNew = !route._id;
  const live = ['active', 'pending_activation'].includes(route.status);
  const [places, setPlaces] = useState([]);
  const [partners, setPartners] = useState([]);
  const [f, setF] = useState({
    originId: route.originId || '', destinationId: route.destinationId || '', stops: (route.stops || []).map(s => s.locationId),
    partnerIds: (route.partnerIds || []).map(String), vehicleIds: (route.vehicleIds || []).map(String),
    availability: route.availability || '', typicalDays: route.typicalDays ?? '', notes: route.notes || '',
    fees: (route.indicativeFees || []).map(x => ({ vehicleType: x.vehicleType, currency: x.currency, amount: String(x.amountMinor / 10 ** decimalsOf(x.currency)) })),
  });
  useEffect(() => {
    api.locations({ active: true }).then(r => setPlaces(r.locations || [])).catch(fail);
    api.partners().then(r => setPartners((r.partners || []).filter(p => p.status === 'approved'))).catch(fail);
  }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const flip = (k, id) => setF(x => ({ ...x, [k]: x[k].includes(id) ? x[k].filter(v => v !== id) : [...x[k], id] }));
  const placeName = p => `${p.name} (${p.countryCode}${p.type !== 'city' && p.type !== 'town' ? `, ${p.type}` : ''})`;
  const currencies = currenciesOf(countries);

  const save = async () => {
    const fees = [];
    for (const x of f.fees) {
      const amountMinor = toMinor(x.amount, x.currency);
      if (amountMinor === null || amountMinor < 0) return toast.error(`Fee "${x.amount}" is not a valid ${x.currency} amount`);
      fees.push({ vehicleType: x.vehicleType, currency: x.currency, amountMinor });
    }
    const vehicleIds = f.vehicleIds.filter(id => partners.some(p => f.partnerIds.includes(String(p._id)) && p.vehicles.some(v => String(v._id) === id)));
    const body = { availability: f.availability, typicalDays: f.typicalDays === '' ? null : Number(f.typicalDays), notes: f.notes, indicativeFees: fees };
    if (!live || !route.crossBorder) Object.assign(body, { partnerIds: f.partnerIds, vehicleIds });
    if (!live) body.stops = f.stops.map(locationId => ({ locationId }));
    try {
      if (isNew) await api.createRoute({ ...body, originId: f.originId, destinationId: f.destinationId }); else await api.updateRoute(route._id, body);
      toast.success('Saved'); onSaved();
    } catch (err) { fail(err); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? 'New route' : `Edit ${route.label}`} size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select label="From" value={f.originId} onChange={set('originId')} disabled={!isNew}>
            <option value="">—</option>{places.map(p => <option key={p._id} value={p._id}>{placeName(p)}{p.isHub ? ' ⭐' : ''}</option>)}
          </Select>
          <Select label="To" value={f.destinationId} onChange={set('destinationId')} disabled={!isNew}>
            <option value="">—</option>{places.map(p => <option key={p._id} value={p._id}>{placeName(p)}</option>)}
          </Select>
        </div>
        <div className="text-xs text-text-secondary">Stops on the way {live && '(locked while the route is live)'}
          <div className="flex gap-2 flex-wrap mt-1">
            {f.stops.map((id, i) => <Badge key={id} label={`${i + 1}. ${places.find(p => p._id === id)?.name || '?'}`} color="cyan" />)}
            {!live && <Select value="" onChange={e => e.target.value && setF(x => ({ ...x, stops: [...x.stops, e.target.value] }))} className="min-w-[160px]">
              <option value="">+ add stop</option>{places.filter(p => ![f.originId, f.destinationId, ...f.stops].includes(p._id)).map(p => <option key={p._id} value={p._id}>{placeName(p)}</option>)}
            </Select>}
            {!live && f.stops.length > 0 && <Button size="xs" variant="ghost" onClick={() => setF(x => ({ ...x, stops: x.stops.slice(0, -1) }))}>Remove last</Button>}
          </div>
        </div>
        <div className="text-xs text-text-secondary">Partners and vehicles {live && route.crossBorder && '(locked while this cross-border route is live)'}
          {partners.map(p => (
            <div key={p._id} className="mt-1">
              <label className="flex gap-1 font-semibold"><input type="checkbox" disabled={live && route.crossBorder} checked={f.partnerIds.includes(String(p._id))} onChange={() => flip('partnerIds', String(p._id))} /> {p.name}</label>
              {f.partnerIds.includes(String(p._id)) && <div className="flex gap-3 ml-5 flex-wrap">{p.vehicles.filter(v => v.isActive).map(v => (
                <label key={v._id} className="flex gap-1"><input type="checkbox" disabled={live && route.crossBorder} checked={f.vehicleIds.includes(String(v._id))} onChange={() => flip('vehicleIds', String(v._id))} /> {v.type} {v.plate}</label>
              ))}</div>}
            </div>
          ))}
          {!partners.length && <div className="text-text-muted">No approved partners yet.</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Availability" placeholder="e.g. Mon, Wed, Fri" value={f.availability} onChange={set('availability')} />
          <Input label="Typical days" type="number" value={f.typicalDays} onChange={set('typicalDays')} />
        </div>
        <div className="text-xs text-text-secondary">Indicative fees (reference only — never charged to a client automatically)
          {f.fees.map((x, i) => (
            <div key={i} className="flex gap-2 mt-1 items-end">
              <Select value={x.vehicleType} onChange={e => setF(y => ({ ...y, fees: y.fees.map((z, j) => j === i ? { ...z, vehicleType: e.target.value } : z) }))}>
                {['any', ...VEHICLE_TYPES].map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
              <Select value={x.currency} onChange={e => setF(y => ({ ...y, fees: y.fees.map((z, j) => j === i ? { ...z, currency: e.target.value } : z) }))}>
                {currencies.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
              <Input value={x.amount} placeholder="amount" onChange={e => setF(y => ({ ...y, fees: y.fees.map((z, j) => j === i ? { ...z, amount: e.target.value } : z) }))} />
              <Button size="xs" variant="ghost" onClick={() => setF(y => ({ ...y, fees: y.fees.filter((_, j) => j !== i) }))}>✕</Button>
            </div>
          ))}
          <Button size="xs" variant="secondary" className="mt-1" onClick={() => setF(y => ({ ...y, fees: [...y.fees, { vehicleType: 'any', currency: currencies[0] || 'UGX', amount: '' }] }))}>+ Fee</Button>
        </div>
        <Input label="Notes (admin only)" value={f.notes} onChange={set('notes')} />
        <div className="flex gap-2"><Button onClick={save} disabled={isNew && (!f.originId || !f.destinationId)}>{isNew ? 'Create (as draft)' : 'Save'}</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

function ChecksModal({ route, onClose }) {
  const [r, setR] = useState(route);
  const [notes, setNotes] = useState(() => Object.fromEntries(CHECKS.map(([k]) => [k, route.verification?.[k]?.note || ''])));
  const save = async (check, done) => {
    try { setR((await api.setCheck(r._id, { check, done, note: notes[check] })).route); toast.success('Saved'); } catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title={`Cross-border checks — ${route.label}`} size="md">
      <p className="text-xs text-text-muted mb-3">Record what you checked (who, which document, when). All 4 are needed before you can ask a second approver.</p>
      <div className="space-y-3">
        {CHECKS.map(([k, name]) => (
          <div key={k} className="border border-border rounded p-2">
            <div className="flex justify-between items-center"><span className="font-semibold text-sm">{name}</span>
              <Badge label={r.verification?.[k]?.done ? `done ${when(r.verification[k].at)}` : 'not done'} color={r.verification?.[k]?.done ? 'green' : 'gray'} /></div>
            <Input value={notes[k]} placeholder="What was checked" onChange={e => setNotes(n => ({ ...n, [k]: e.target.value }))} />
            <div className="flex gap-1 mt-1">
              <Button size="xs" onClick={() => save(k, true)} disabled={!notes[k].trim()}>Mark done</Button>
              {r.verification?.[k]?.done && <Button size="xs" variant="ghost" onClick={() => save(k, false)}>Undo</Button>}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ── Dispatch ─────────────────────────────────────────────────────────────────
function DispatchTab({ countries }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.dispatches().then(r => setRows(r.dispatches || [])).catch(fail), []);
  useEffect(() => { load(); }, [load]);
  const move = async (d, status) => {
    let note = '';
    if (status === 'cancelled') { note = ask(`Why is ${d.ref} cancelled? (its unpaid trip entry is reversed)`); if (!note) return; }
    try { await api.dispatchStatus(d._id, { status, note }); load(); } catch (err) { fail(err); }
  };
  return (
    <div>
      <p className="text-xs text-text-muted mb-3">
        Hand an order or request to a partner on a route. This is a separate record — the order and its delivery fee are not changed;
        keep using the order's own “Dispatched” button for the client.
      </p>
      <Button size="sm" className="mb-3" onClick={() => setAdding(true)}>+ New dispatch</Button>
      {rows === null ? <Spinner /> : rows.length === 0 ? <Empty title="No dispatches yet" /> : (
        <Table headers={['Ref', 'For', 'Route', 'Partner / vehicle', 'Status', '']}>
          {rows.map(d => (
            <tr key={d._id}>
              <Td className="font-mono text-xs">{d.ref}<div className="text-text-muted">{when(d.createdAt)}</div></Td>
              <Td>{d.subjectRef}</Td>
              <Td><div className="text-xs">{d.snapshot.route}</div></Td>
              <Td><div className="text-xs">{d.snapshot.partner}<div className="text-text-muted">{d.snapshot.vehicle}</div></div></Td>
              <Td><Badge label={label(d.status)} color={DISPATCH_COLOR[d.status]} /></Td>
              <Td><div className="flex gap-1">
                {d.status === 'assigned' && <Button size="xs" onClick={() => move(d, 'in_transit')}>On the road</Button>}
                {d.status === 'in_transit' && <Button size="xs" onClick={() => move(d, 'delivered')}>Delivered</Button>}
                {['assigned', 'in_transit'].includes(d.status) && <Button size="xs" variant="ghost" onClick={() => move(d, 'cancelled')}>Cancel</Button>}
              </div></Td>
            </tr>
          ))}
        </Table>
      )}
      {adding && <DispatchModal countries={countries} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function DispatchModal({ countries, onClose, onSaved }) {
  const [routes, setRoutes] = useState([]);
  const [partners, setPartners] = useState([]);
  const currencies = currenciesOf(countries);
  const [f, setF] = useState({ subjectType: 'dwisend_order', subjectRef: '', routeId: '', partnerId: '', vehicleId: '', amount: '', currency: currencies[0] || 'UGX', notes: '' });
  useEffect(() => {
    api.routes({ status: 'active' }).then(r => setRoutes((r.routes || []).filter(x => x.bookable))).catch(fail);
    api.partners().then(r => setPartners(r.partners || [])).catch(fail);
  }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const route = routes.find(r => r._id === f.routeId);
  const partner = partners.find(p => p._id === f.partnerId);
  const vehicles = (partner?.vehicles || []).filter(v => v.isActive && (!route?.vehicleIds.length || route.vehicleIds.map(String).includes(String(v._id))));

  const save = async () => {
    const body = { subjectType: f.subjectType, subjectRef: f.subjectRef.trim(), routeId: f.routeId, partnerId: f.partnerId, vehicleId: f.vehicleId, notes: f.notes };
    if (f.amount.trim()) {
      const amountMinor = toMinor(f.amount, f.currency);
      if (!amountMinor || amountMinor < 0) return toast.error(`"${f.amount}" is not a valid ${f.currency} amount`);
      body.owed = { amountMinor, currency: f.currency };
    }
    try { const r = await api.createDispatch(body); toast.success(`${r.dispatch.ref} recorded${r.entry ? ` — ${money(r.entry.amountMinor, r.entry.currency)} owed` : ''}`); onSaved(); }
    catch (err) { fail(err); }
  };

  return (
    <Modal open onClose={onClose} title="New dispatch" size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select label="What" value={f.subjectType} onChange={set('subjectType')}>{SUBJECTS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</Select>
          <Input label="Reference" placeholder="DSO-… / MKT-… / DRQ-…" value={f.subjectRef} onChange={set('subjectRef')} />
        </div>
        <Select label="Route (active, open only)" value={f.routeId} onChange={e => setF(x => ({ ...x, routeId: e.target.value, partnerId: '', vehicleId: '' }))}>
          <option value="">—</option>{routes.map(r => <option key={r._id} value={r._id}>{r.label}</option>)}
        </Select>
        {route && <>
          <Select label="Partner" value={f.partnerId} onChange={e => setF(x => ({ ...x, partnerId: e.target.value, vehicleId: '' }))}>
            <option value="">—</option>{route.partners.filter(p => p.status === 'approved').map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Select label="Vehicle" value={f.vehicleId} onChange={set('vehicleId')}>
            <option value="">—</option>{vehicles.map(v => <option key={v._id} value={v._id}>{v.type} {v.plate}</option>)}
          </Select>
          {route.indicativeFees.length > 0 && <p className="text-xs text-text-muted">Indicative: {route.indicativeFees.map(x => `${x.vehicleType} ${money(x.amountMinor, x.currency)}`).join(' · ')} — a reference only; type what you agreed.</p>}
        </>}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Owed to the partner (optional)" placeholder="agreed trip price" value={f.amount} onChange={set('amount')} />
          <Select label="Currency" value={f.currency} onChange={set('currency')}>{currencies.map(c => <option key={c} value={c}>{c}</option>)}</Select>
        </div>
        <Input label="Notes (admin only)" value={f.notes} onChange={set('notes')} />
        <div className="flex gap-2"><Button onClick={save} disabled={!f.subjectRef.trim() || !f.routeId || !f.partnerId || !f.vehicleId}>Record dispatch</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

// ── Ledger ───────────────────────────────────────────────────────────────────
function LedgerTab({ countries }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState(null);
  const load = useCallback(() => api.ledger().then(setData).catch(fail), []);
  useEffect(() => { load(); }, [load]);
  const reverse = async e => {
    const why = ask(`Why is ${e.ref} reversed? (only for a mistake — it stays on record)`); if (!why) return;
    try { await api.reverseEntry(e._id, why); load(); } catch (err) { fail(err); }
  };
  if (!data) return <Spinner />;
  return (
    <div>
      <p className="text-xs text-text-muted mb-3">
        What DWI owes transport partners — records only. Pay outside the app (MoMo, cash, bank), then “Mark paid”; a different
        DwiSend approver confirms it with their authenticator code. Entries are never edited or deleted.
      </p>
      {data.owed.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3">{data.owed.map(o => (
          <Badge key={`${o.partnerId}${o.currency}`} label={`${o.partner}: ${money(o.amountMinor, o.currency)} still owed (${o.entries})`} color={o.amountMinor < 0 ? 'gray' : 'amber'} />
        ))}</div>
      )}
      <Button size="sm" className="mb-3" onClick={() => setAdding(true)}>+ Adjustment</Button>
      {data.entries.length === 0 ? <Empty title="No entries yet" description="A trip entry is added when you record a dispatch with an amount owed." /> : (
        <Table headers={['Ref', 'Partner', 'What', 'Amount', 'Status', '']}>
          {data.entries.map(e => (
            <tr key={e._id} className={e.status === 'reversed' ? 'opacity-50' : ''}>
              <Td className="font-mono text-xs">{e.ref}<div className="text-text-muted">{when(e.createdAt)}</div></Td>
              <Td>{e.snapshot.partner}</Td>
              <Td><div className="text-xs max-w-[260px]">{e.description}{e.snapshot.dispatch && <div className="text-text-muted">{e.snapshot.dispatch}</div>}
                {e.reference && <div className="text-text-muted">{label(e.method)} · {e.reference}</div>}{e.reverseReason && <div className="text-text-muted">Reversed: {e.reverseReason}</div>}</div></Td>
              <Td className="font-semibold">{money(e.amountMinor, e.currency)}</Td>
              <Td><Badge label={e.status === 'pending_paid_approval' ? 'waiting for approver' : label(e.status)} color={LEDGER_COLOR[e.status]} /></Td>
              <Td><div className="flex gap-1">
                {e.status === 'due' && e.amountMinor > 0 && <Button size="xs" onClick={() => setPaying(e)}>Mark paid</Button>}
                {e.status === 'due' && <Button size="xs" variant="ghost" onClick={() => reverse(e)}>Reverse</Button>}
              </div></Td>
            </tr>
          ))}
        </Table>
      )}
      {adding && <AdjustmentModal countries={countries} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {paying && <MarkPaidModal entry={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); load(); }} />}
    </div>
  );
}

function AdjustmentModal({ countries, onClose, onSaved }) {
  const [partners, setPartners] = useState([]);
  const currencies = currenciesOf(countries);
  const [f, setF] = useState({ partnerId: '', amount: '', currency: currencies[0] || 'UGX', description: '' });
  useEffect(() => { api.partners().then(r => setPartners(r.partners || [])).catch(fail); }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    const amountMinor = toMinor(f.amount, f.currency);
    if (!amountMinor) return toast.error(`"${f.amount}" is not a valid ${f.currency} amount`);
    try { await api.addEntry({ partnerId: f.partnerId, kind: 'adjustment', amountMinor, currency: f.currency, description: f.description }); toast.success('Recorded'); onSaved(); }
    catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title="Ledger adjustment" size="sm">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">Positive = DWI owes the partner more. Negative = the partner owes DWI (e.g. an advance returned).</p>
        <Select label="Partner" value={f.partnerId} onChange={set('partnerId')}><option value="">—</option>{partners.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}</Select>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Amount" placeholder="-50000" value={f.amount} onChange={set('amount')} />
          <Select label="Currency" value={f.currency} onChange={set('currency')}>{currencies.map(c => <option key={c} value={c}>{c}</option>)}</Select>
        </div>
        <Input label="Why" value={f.description} onChange={set('description')} />
        <div className="flex gap-2"><Button onClick={save} disabled={!f.partnerId || !f.amount.trim() || !f.description.trim()}>Record</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

function MarkPaidModal({ entry, onClose, onSaved }) {
  const [f, setF] = useState({ method: 'mobile_money', reference: '' });
  const save = async () => {
    try { await api.markPaid(entry._id, f); toast.success('Marked paid — waiting for a second approver'); onSaved(); } catch (err) { fail(err); }
  };
  return (
    <Modal open onClose={onClose} title={`Mark paid — ${entry.ref}`} size="sm">
      <div className="space-y-3">
        <p className="text-sm">{entry.snapshot.partner}: <strong>{money(entry.amountMinor, entry.currency)}</strong>. Only mark it once the money has actually been sent outside the app.</p>
        <Select label="Paid by" value={f.method} onChange={e => setF(x => ({ ...x, method: e.target.value }))}>
          {['mobile_money', 'cash', 'bank', 'other'].map(m => <option key={m} value={m}>{label(m)}</option>)}
        </Select>
        <Input label="Transaction reference" placeholder="MoMo / bank ref (optional for cash)" value={f.reference} onChange={e => setF(x => ({ ...x, reference: e.target.value }))} />
        <div className="flex gap-2"><Button onClick={save}>Mark paid</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

// ── Approvals (admins and DwiSend approvers) ─────────────────────────────────
export function ApprovalsList() {
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(null);   // { kind: 'route'|'entry', item }
  const load = useCallback(() => api.approvals().then(setData).catch(fail), []);
  useEffect(() => { load(); }, [load]);
  const turnDown = async (kind, item) => {
    const why = ask('Why? / Pourquoi ?'); if (!why) return;
    try { if (kind === 'route') await api.rejectActivation(item._id, why); else await api.rejectPaid(item._id, why); toast.success('Turned down'); load(); }
    catch (err) { fail(err); }
  };
  if (!data) return <Spinner />;
  if (!data.routes.length && !data.entries.length) return <Empty title="Nothing waiting / Rien en attente" description="Cross-border routes and partner payments that need a second person appear here." />;
  return (
    <div className="space-y-4">
      {data.routes.length > 0 && <div>
        <h3 className="font-semibold text-sm mb-2">Cross-border routes to activate / Routes transfrontalières à activer</h3>
        {data.routes.map(r => (
          <div key={r._id} className="border border-border rounded-lg p-3 mb-2">
            <div className="font-semibold">{r.label}</div>
            <div className="text-xs text-text-muted">Asked by {r.requestedBy} · {when(r.requestedAt)} · partners: {r.partners.map(p => p.name).join(', ')} · vehicles: {r.vehicles.map(v => v.plate).join(', ')}</div>
            <ul className="text-xs mt-1">{CHECKS.map(([k, n]) => <li key={k}>✔ {n}: {r.verification?.[k]?.note}</li>)}</ul>
            <div className="flex gap-1 mt-2">
              {r.yours ? <span className="text-xs text-text-muted">You asked for this — a different approver must approve it.</span>
                : <Button size="xs" onClick={() => setApproving({ kind: 'route', item: r })}>Approve / Approuver</Button>}
              <Button size="xs" variant="ghost" onClick={() => turnDown('route', r)}>Turn down / Refuser</Button>
            </div>
          </div>
        ))}
      </div>}
      {data.entries.length > 0 && <div>
        <h3 className="font-semibold text-sm mb-2">Partner payments to confirm / Paiements partenaires à confirmer</h3>
        {data.entries.map(e => (
          <div key={e._id} className="border border-border rounded-lg p-3 mb-2">
            <div className="font-semibold">{e.partner}: {money(e.amountMinor, e.currency)} <span className="font-mono text-xs text-text-muted">{e.ref}</span></div>
            <div className="text-xs text-text-muted">{e.description} · {label(e.method)} {e.reference} · marked by {e.markedBy} {when(e.markedAt)}</div>
            <div className="flex gap-1 mt-2">
              {e.yours ? <span className="text-xs text-text-muted">You marked this — a different approver must confirm it.</span>
                : <Button size="xs" onClick={() => setApproving({ kind: 'entry', item: e })}>Confirm paid / Confirmer</Button>}
              <Button size="xs" variant="ghost" onClick={() => turnDown('entry', e)}>Turn down / Refuser</Button>
            </div>
          </div>
        ))}
      </div>}
      {approving && <ApproveModal {...approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); load(); }} />}
    </div>
  );
}

function ApproveModal({ kind, item, onClose, onDone }) {
  const [confirmed, setConfirmed] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      if (kind === 'route') await api.approveActivation(item._id, { confirmed, code }); else await api.approvePaid(item._id, { confirmed, code });
      toast.success(kind === 'route' ? 'Route active / Route active' : 'Confirmed paid / Paiement confirmé'); onDone();
    } catch (err) { fail(err); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={kind === 'route' ? `Approve ${item.label}` : `Confirm ${item.ref}`} size="sm">
      <div className="space-y-3">
        <label className="flex gap-2 text-sm">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
          {kind === 'route'
            ? 'I checked the partners, transport, permits and arrangements. / J\'ai vérifié partenaires, transport, permis et arrangements.'
            : 'I checked this payment on the real statement. / J\'ai vérifié ce paiement sur le vrai relevé.'}
        </label>
        <Input label="Authenticator code / Code de l'application" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value)} />
        <div className="flex gap-2"><Button onClick={go} loading={busy} disabled={!confirmed || code.length !== 6}>Approve / Approuver</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
      </div>
    </Modal>
  );
}

// /dwisend-logistics-approvals — for DwiSend approvers who aren't admins.
export default function DwiSendLogisticsApprovals() {
  const [st, setSt] = useState(null);
  const [blocked, setBlocked] = useState(null);
  useEffect(() => { api.status().then(setSt).catch(err => setBlocked(err)); }, []);
  if (blocked) return <Card><SectionHeader title="✅ DwiSend logistics approvals" /><p className="text-sm text-text-secondary">{blocked.status === 403 ? 'This page is for DwiSend approvers. / Page réservée aux approbateurs DwiSend.' : blocked.message}</p></Card>;
  if (!st) return <div className="py-10 flex justify-center"><Spinner /></div>;
  return (
    <Card>
      <SectionHeader title="✅ DwiSend logistics approvals / Approbations logistique" />
      {!st.enabled ? <p className="text-sm text-text-secondary">DwiSend logistics is switched off — nothing to approve. / La logistique DwiSend est désactivée.</p>
        : <>
          {st.youAreApprover && !st.twoFactorReady && <p className="text-sm text-amber-400 mb-3">Set up two-step login first (Settings → Two-Factor Authentication). / Activez d'abord la double authentification.</p>}
          <ApprovalsList />
        </>}
    </Card>
  );
}
